<template>
    <rc-in-app-notice-dialog
        notice_type="toll-free-registration"
        notice_location="home"
        title="Toll-Free Registration Information"
        max-width="800"
        :loading="loading"
        persistent
        :remind_me_later_text="is_touched ? 'Save & Finish Later' : 'Remind Me Later'"
        complete_text="Finish & Submit"
        :can_complete="is_form_valid"
        @shown="loadFormData"
        @remind="onFinishLater"
        @complete="onSubmit"
    >
        <div class="px-2 py-4">
            <div class="px-2">
                <p class="mb-3 text-body-2">
                    We're adding a toll-free number as a backup messaging option. Because local (10DLC) and toll-free messaging operate as separate systems, this helps reduce disruption if one pathway experiences deliverability issues. There's no meaningful cost impact — just more flexibility. A toll-free number costs less than $1/month.
                </p>
                <p class="mb-5 text-body-2">
                    Please complete the information below so we can register this for you.
                </p>
            </div>

            <v-expansion-panels
                class="d-flex flex-column gap-1"
                v-model="active_panel"
                accordion
                flat
                @focusin.native.once="is_touched = true"
            >
                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="org_info_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ org_info_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Organization Info</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Legal Company Name"
                            type="text"
                            v-model="form_data.organization_legal_name"
                            hint="This should match exactly what the IRS has on file for your company's EIN."
                            required
                            @validated="v => onFieldValidated('organization_legal_name', v)"
                        />

                        <v-checkbox
                            label="My business has a different DBA (Doing Business As)"
                            class="mt-2 mb-0"
                            v-model="form_data.has_dba"
                            hide-details
                            @change="form_data.has_dba && $nextTick(() => $refs['dba-input']?.focus())"
                        />

                        <transition name="fade">
                            <rc-base-input
                                v-if="form_data.has_dba"
                                ref="dba-input"
                                label="DBA"
                                class="mt-4"
                                v-model="form_data.dba"
                                hint="DBA or Brand Name, if different from legal name"
                                required
                                @validated="v => onFieldValidated('dba', v)"
                            />
                        </transition>

                        <rc-select
                            label="Legal Form"
                            class="mt-4"
                            v-model="form_data.tcr_legal_form"
                            :items="$rc.enumChoices('tcr_organiztion_legal_form', false)"
                            hint="Choose which best describes your organization. Most RedChirp customers choose 'Private Company'"
                            required
                            @validated="v => onFieldValidated('tcr_legal_form', v)"
                        />

                        <rc-base-input
                            type="us-or-ca-tax-id"
                            label="Tax Identification Number (EIN)"
                            class="mt-4"
                            v-model="form_data.tax_id"
                            required
                            @validated="v => onFieldValidated('tax_id', v)"
                        >
                            <template #hint>
                                <div class="d-flex align-center">
                                    <span class="mr-2">Your organization's Tax ID</span>
                                    <v-tooltip bottom>
                                        <template #activator="{ on, attrs }">
                                            <v-icon
                                                small
                                                color="grey"
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                mdi-help-circle
                                            </v-icon>
                                        </template>
                                        <div>
                                            <p class="mb-0"><strong>US:</strong> Use your 9-digit Employer Identification Number (EIN)</p>
                                            <p class="mb-0"><strong>Canada:</strong> Nine Digit Business Number (BN 9 - not BN15)</p>
                                        </div>
                                    </v-tooltip>
                                </div>
                            </template>
                        </rc-base-input>

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 1"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="contact_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ contact_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Business Contact</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <p class="text-body-2 grey--text text--darken-1">The contact information for your organization's primary, individual contact who should be called or emailed if there is any issue with your toll-free registration.</p>

                        <v-row class="mb-0">
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="Contact First Name"
                                    v-model="form_data.business_contact_first_name"
                                    class="mb-0"
                                    required
                                    @validated="v => onFieldValidated('business_contact_first_name', v)"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="Contact Last Name"
                                    v-model="form_data.business_contact_last_name"
                                    class="mb-0"
                                    required
                                    @validated="v => onFieldValidated('business_contact_last_name', v)"
                                />
                            </v-col>
                        </v-row>

                        <rc-base-input
                            type="tel"
                            label="Contact Phone Number"
                            class="mt-2"
                            v-model="form_data.tcr_phone_number"
                            hint="The Campaign Registry requires you to provide an official phone number (not a personal one)."
                            required
                            @validated="v => onFieldValidated('tcr_phone_number', v)"
                        />

                        <rc-base-input
                            type="official-email"
                            label="Contact Email"
                            class="mt-4"
                            hint="The Campaign Registry requires you to provide an official email address (not a personal one). This email address is used in your default response to the HELP CTIA keyword. For example, john_doe@yourcompany.com instead of john_doe@gmail.com or support@yourcompany.com."
                            v-model="form_data.tcr_contact_email"
                            required
                            @validated="v => onFieldValidated('tcr_contact_email', v)"
                        />

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 2"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="address_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ address_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Business Address</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Street Address"
                            v-model="form_data.business_address"
                            hint="This should match exactly what's on file with the IRS and/or your state/province's business registration office"
                            required
                            @validated="v => onFieldValidated('business_address', v)"
                        />

                        <v-row>
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="City"
                                    v-model="form_data.business_address_city"
                                    required
                                    @validated="v => onFieldValidated('business_address_city', v)"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    :label="state_or_province"
                                    v-model="form_data.business_address_state"
                                    required
                                    @validated="v => onFieldValidated('business_address_state', v)"
                                />
                            </v-col>
                        </v-row>

                        <v-row class="mt-0">
                            <v-col cols="12" sm="6">
                                <rc-base-input
                                    label="Zip / Postal Code"
                                    v-model="form_data.business_address_zip"
                                    required
                                    @validated="v => onFieldValidated('business_address_zip', v)"
                                />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <rc-select
                                    label="Country"
                                    v-model="form_data.country"
                                    :items="$rc.enumChoices('tcr_country', false)"
                                    required
                                    @validated="v => onFieldValidated('country', v)"
                                />
                            </v-col>
                        </v-row>

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 3"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="website_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ website_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Website</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Website URL"
                            v-model="form_data.website"
                            hint="Your organization's primary website (e.g. for Microsoft, https://www.microsoft.com)"
                            required
                            @validated="v => onFieldValidated('website', v)"
                            @blur="onUrlBlur('website')"
                        />

                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                small
                                @click="active_panel = 4"
                            >
                                Next
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                    <v-expansion-panel-header class="shaded-light">
                        <div class="d-flex align-center">
                            <v-icon
                                :color="policies_complete ? 'success' : 'grey'"
                                class="mr-2"
                                small
                            >
                                {{ policies_complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                            </v-icon>
                            <span class="font-weight-bold">Policies</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pt-4" eager>
                        <rc-base-input
                            label="Privacy Policy URL"
                            v-model="form_data.url_privacy_policy"
                            hint="The web address (URL) for your organization's privacy policy (e.g. for Microsoft, https://privacy.microsoft.com/en-us/privacy-statement)"
                            required
                            @validated="v => onFieldValidated('url_privacy_policy', v)"
                            @blur="onUrlBlur('url_privacy_policy')"
                        />

                        <rc-select
                            class="mt-4"
                            label=""
                            v-model="form_data.privacy_policy_addendum"
                            :items="privacy_policy_addendum_options"
                            required
                            @validated="v => onFieldValidated('privacy_policy_addendum', v)"
                        >
                            <template #label>
                                <span class="text-body-2">
                                    Privacy Policy Addendum <span class="error--text">*</span>
                                </span>
                            </template>
                        </rc-select>

                        <div class="text-caption mt-1 mb-2 d-flex align-center">
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            :to="{ name: 'default-privacy-policy-addendum', params: { organization_id } }"
                                            text="Privacy policy addendum template"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Our privacy policy addendum template, tailored for your organization</span>
                            </v-tooltip>
                            <span class="mx-2 grey--text">|</span>
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            href="https://redchirp.com/texting-requirements-of-your-privacy-policy/"
                                            text="Carrier requirements"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Examples of acceptable privacy policy language for crafting your own</span>
                            </v-tooltip>
                        </div>

                        <v-divider class="my-4" />

                        <rc-base-input
                            label="Terms and Conditions URL"
                            class="mt-4"
                            v-model="form_data.url_terms_and_conditions"
                            hint="The web address (URL) for your organization's terms and conditions (e.g. for Microsoft, https://www.microsoft.com/en-us/legal/terms-of-use)"
                            required
                            @validated="v => onFieldValidated('url_terms_and_conditions', v)"
                            @blur="onUrlBlur('url_terms_and_conditions')"
                        />

                        <rc-select
                            class="mt-4"
                            label=""
                            v-model="form_data.terms_and_conditions_addendum"
                            :items="terms_and_conditions_addendum_options"
                            required
                            @validated="v => onFieldValidated('terms_and_conditions_addendum', v)"
                        >
                            <template #label>
                                <span class="text-body-2">
                                    Terms and Conditions Addendum <span class="error--text">*</span>
                                </span>
                            </template>
                        </rc-select>

                        <div class="text-caption mt-1 mb-2 d-flex align-center">
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            :to="{ name: 'default-terms-and-conditions-addendum', params: { organization_id } }"
                                            text="Terms and conditions addendum template"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Our terms and conditions addendum template, tailored for your organization</span>
                            </v-tooltip>
                            <span class="mx-2 grey--text">|</span>
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <rc-link
                                            href="https://redchirp.com/texting-requirements-and-your-websites-terms-and-conditions/"
                                            text="Carrier requirements"
                                            text_transform="capitalize"
                                        />
                                    </span>
                                </template>
                                <span>Examples of acceptable terms and conditions language for crafting your own</span>
                            </v-tooltip>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <div class="mt-4 px-2 text-caption text-center grey--text">
                {{ sections_complete }} of 5 sections complete
            </div>
        </div>
    </rc-in-app-notice-dialog>
</template>

<script>
export default {
    data() {
        return {
            loading: false,
            active_panel: null,
            is_touched: false,
            form_data: {},
            field_validity: {},
            privacy_policy_addendum_options: [
                { text: 'Adopt and use the RedChirp text messaging privacy policy addendum', value: 'use-form-addendum' },
                { text: 'We have updated our website\'s privacy policy to satisfy carrier requirements', value: 'do-not-use-form-addendum' },
            ],
            terms_and_conditions_addendum_options: [
                { text: 'Adopt and use the RedChirp text messaging terms and conditions addendum', value: 'use-form-addendum' },
                { text: 'We have updated our website\'s terms and conditions to satisfy carrier requirements', value: 'do-not-use-form-addendum' },
            ],
        };
    },

    computed: {
        organization_id() {
            return this.$rc.$state.profile.employee_account.organization_id;
        },

        state_or_province() {
            return !this.form_data.country || this.form_data.country === 'us' ? 'State' : 'Province';
        },

        org_info_complete() {
            if (!this.fieldsValid('organization_legal_name')) return false;
            if (this.form_data.has_dba && !this.fieldsValid('dba')) return false;
            if (!this.fieldsValid('tcr_legal_form')) return false;
            if (!this.fieldsValid('tax_id')) return false;
            return true;
        },

        contact_complete() {
            return this.fieldsValid(
                'business_contact_first_name',
                'business_contact_last_name',
                'tcr_contact_email',
                'tcr_phone_number',
            );
        },

        address_complete() {
            return this.fieldsValid(
                'business_address',
                'business_address_city',
                'business_address_state',
                'business_address_zip',
                'country'
            );
        },

        website_complete() {
            return this.fieldsValid('website');
        },

        policies_complete() {
            return this.fieldsValid(
                'url_privacy_policy',
                'privacy_policy_addendum',
                'url_terms_and_conditions',
                'terms_and_conditions_addendum'
            );
        },

        sections_complete() {
            let count = 0;
            if (this.org_info_complete) count++;
            if (this.contact_complete) count++;
            if (this.address_complete) count++;
            if (this.website_complete) count++;
            if (this.policies_complete) count++;
            return count;
        },

        is_form_valid() {
            return this.sections_complete === 5;
        },
    },

    methods: {
        fieldsValid(...fields) {
            return fields.every(f => this.field_validity[f] === true);
        },

        onFieldValidated(field, is_valid) {
            this.$set(this.field_validity, field, is_valid);
        },

        loadFormData() {
            this.loading = true;

            this.$rc.rest({
                spinner: false,
                endpoint: `GET: /api/v1/welcome/tcr-organization-setup-data/${this.organization_id}`,
                on_success: (data) => {
                    this.form_data = { ...(data || {}) };
                    this.loading = false;
                },
                on_error: () => {
                    // fail silently
                    console.error('Failed to load TCR organization setup data');
                    this.loading = false;
                }
            });
        },

        onUrlBlur(field) {
            const val = this.form_data[field];
            if (val) {
                this.form_data[field] = this.$rc.maskUrl(val);
            }
        },

        onFinishLater() {
            if (!this.is_touched) {
                this.$rc.snackbar(`We'll remind you once a day until the registration is completed for your organization`);
                return;
            }

            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/welcome/tcr-organization-setup-data',
                params: {
                    organization_id: this.organization_id,
                    ...this.form_data,
                },

                on_success: () => {
                    this.$rc.snackbar(`Progress saved! We'll remind you once a day until the registration is completed for your organization`);
                },

                on_error: () => {
                    this.$rc.snackbar('Unable to save progress. Please try again later.');
                },
            });
        },

        onSubmit() {
            if (!this.is_form_valid) {
                this.$rc.snackbar('Please complete all required fields');
                return;
            }

            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/welcome/tcr-organization-setup-data',
                params: {
                    organization_id: this.organization_id,
                    ...this.form_data,
                },

                on_success: () => {
                    this.$rc.snackbar('Registration information submitted successfully!');
                },

                on_error: () => {
                    this.$refs.periodicDialog.onDismiss();
                    this.$rc.snackbar('Unable to submit registration data. Please try again later.');
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
</style>